import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Heading,
    FormControl,
    FormLabel,
    Input,
    Checkbox,
    Button,
    Alert,
    AlertIcon,
    useToast,
    FormErrorMessage,
    Select
} from '@chakra-ui/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';

const RegistroConductor = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();

    // Estado inicial del formulario con solo los campos obligatorios y algunos opcionales visibles
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        nombre: '',
        apellidos: '', // Obligatorio
        telefono: '', // Obligatorio
        habilitado: false, // Oculto, valor por defecto false
        activo: false, // Obligatorio (aunque no visible en este formulario simplificado)
        acepto_politicas: false, // Obligatorio
        forma_pago: 'Bizum', // Obligatorio (valor por defecto)
        matricula_vehiculo: '', // Obligatorio
        licencia_conducir: '', // Obligatorio
        cargo: 'conductor', // Obligatorio (valor por defecto)
        cif: '', // Obligatorio
        tipo_ecotaxi: '', // Obligatorio
        sillita_bebe: false, // Opcional, visible
        apto_minusvalidos: false, // Opcional, visible
        telefono2: '', // Oculto, valor por defecto ''
        direccion: '', // Oculto, valor por defecto ''
        idioma: 'es', // Opcional, visible, valor por defecto 'es'
        comentarios: '', // Oculto, valor por defecto ''
        declaracion_responsable: false, // Obligatorio
        acepto_normativa_over: false, // Obligatorio
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevFormData) => {
            const newFormData = {
                ...prevFormData,
                [name]: type === 'checkbox' ? checked : value,
            };

            if (name === 'password') {
                validatePassword(newFormData.password);
            }
            return newFormData;
        });
    };

    const validatePassword = (password) => {
        let currentPasswordError = '';
        if (password.length < 8) {
            currentPasswordError = 'La contraseña debe tener al menos 8 caracteres.';
        } else if (!/[a-z]/.test(password)) {
            currentPasswordError = 'La contraseña debe contener al menos una minúscula.';
        } else if (!/[A-Z]/.test(password)) {
            currentPasswordError = 'La contraseña debe contener al menos una mayúscula.';
        } else if (!/[0-9]/.test(password)) {
            currentPasswordError = 'La contraseña debe contener al menos un número.';
        }

        setPasswordError(currentPasswordError);
        return currentPasswordError === '';
    };

    useEffect(() => {
        const isEmailValid = formData.email !== '';
        const isPasswordValid = formData.password !== '' && validatePassword(formData.password);
        const isNombreValid = formData.nombre !== '';
        const isApellidosValid = formData.apellidos !== '';
        const isTelefonoValid = formData.telefono !== '';
        const isMatriculaValid = formData.matricula_vehiculo.length >= 6 && formData.matricula_vehiculo.length <= 20;
        const isLicenciaValid = formData.licencia_conducir.length >= 1 && formData.licencia_conducir.length <= 5;
        const isCargoValid = formData.cargo === 'titular' || formData.cargo === 'conductor';
        const isCifValid = formData.cif.length >= 8 && formData.cif.length <= 15;
        const isPoliticasAceptadas = formData.acepto_politicas;
        const isDeclaracionResponsable = formData.declaracion_responsable;
        const isNormativaOverAceptada = formData.acepto_normativa_over;
        const isTipoEcotaxiValid = formData.tipo_ecotaxi !== '';

        setIsFormValid(
            isEmailValid &&
            isPasswordValid &&
            isNombreValid &&
            isApellidosValid &&
            isTelefonoValid &&
            isMatriculaValid &&
            isLicenciaValid &&
            isCargoValid &&
            isCifValid &&
            isPoliticasAceptadas &&
            isDeclaracionResponsable &&
            isNormativaOverAceptada &&
            isTipoEcotaxiValid
        );
    }, [formData, passwordError]);

    const handleSubmit = async (event) => {
      event.preventDefault();

      if (!isFormValid) {
          toast({
              title: 'Formulario incompleto o inválido',
              description: 'Por favor, revisa todos los campos requeridos y errores de contraseña.',
              status: 'warning',
              duration: 5000,
              isClosable: true,
          });
          return;
      }

      setIsSubmitting(true);
      setError('');

      try {
          const baseURL = process.env.REACT_APP_API_BASE_URL;
          const url = `${baseURL}/api/conductores/`;
          const response = await fetch(url, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify(formData),
          });

          if (response.ok) {
              toast({
                  title: 'Conductor registrado',
                  description: 'El conductor ha sido registrado exitosamente.',
                  status: 'success',
                  duration: 5000,
                  isClosable: true,
              });
              navigate('/login'); // Redirigir a la lista de conductores
          } else {
              const errorData = await response.json();
              let errorMessage = `Error al crear conductor: ${response.status} - ${errorData?.detail || response.statusText}`;

              // Manejar errores específicos del backend
              if (response.status === 409 && errorData?.detail === "El correo electrónico ya está registrado.") {
                  errorMessage = "El correo electrónico que has introducido ya está registrado.";
              } else if (response.status === 409 && errorData?.detail === "La matrícula del vehículo ya está registrada.") {
                  errorMessage = "La matrícula del vehículo que has introducido ya está registrada.";
              } else if (response.status === 409 && errorData?.detail === "El número de licencia de conducir ya está registrado.") {
                  errorMessage = "El número de licencia de conducir que has introducido ya está registrado.";
              }

              setError(errorMessage);
              toast({
                  title: 'Error al registrar conductor',
                  description: errorMessage,
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
              });
          }
      } catch (err) {
          setError(`Error de conexión: ${err.message}`);
          toast({
              title: 'Error de conexión',
              description: err.message,
              status: 'error',
              duration: 5000,
              isClosable: true,
          });
      } finally {
          setIsSubmitting(false);
      }
  };

    return (
        <Box p={4} borderWidth="1px" borderRadius="lg" overflow="hidden" maxWidth="md" mx="auto">
            <Heading mb={4}>{t('registro_conductor_titulo')}</Heading>
            {error && (
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    {error}
                </Alert>
            )}
            <form onSubmit={handleSubmit}>
                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="email">{t('registro_conductor_email')}</FormLabel>
                    <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired isInvalid={!!passwordError}>
                    <FormLabel htmlFor="password">{t('registro_conductor_password')}</FormLabel>
                    <Input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                    />
                    {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="nombre">{t('registro_conductor_nombre')}</FormLabel>
                    <Input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="apellidos">{t('registro_conductor_apellidos')}</FormLabel>
                    <Input type="text" id="apellidos" name="apellidos" value={formData.apellidos} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="telefono">{t('registro_conductor_telefono')}</FormLabel>
                    <PhoneInput
                        country={'es'}
                        value={formData.telefono}
                        onChange={(value) => setFormData({ ...formData, telefono: value })}
                        inputProps={{ name: 'telefono', required: true, autoFocus: true }}
                    />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="matricula_vehiculo">{t('registro_conductor_matricula')}</FormLabel>
                    <Input type="text" id="matricula_vehiculo" name="matricula_vehiculo" value={formData.matricula_vehiculo} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="licencia_conducir">{t('registro_conductor_licencia')}</FormLabel>
                    <Input type="text" id="licencia_conducir" name="licencia_conducir" value={formData.licencia_conducir} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="cargo">{t('registro_conductor_cargo')}</FormLabel>
                    <Select id="cargo" name="cargo" value={formData.cargo} onChange={handleChange}>
                        <option value="titular">{t('registro_conductor_cargo_titular')}</option>
                        <option value="conductor">{t('registro_conductor_cargo_conductor')}</option>
                    </Select>
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="cif">{t('registro_conductor_cif')}</FormLabel>
                    <Input type="text" id="cif" name="cif" value={formData.cif} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4} isRequired>
                    <FormLabel htmlFor="tipo_ecotaxi">{t('registro_conductor_tipo_ecotaxi')}</FormLabel>
                    <Select
                        id="tipo_ecotaxi"
                        name="tipo_ecotaxi"
                        value={formData.tipo_ecotaxi}
                        onChange={handleChange}
                        placeholder={t('registro_conductor_tipo_ecotaxi_placeholder')}
                    >
                        <option value="híbrido">{t('registro_conductor_tipo_hibrido')}</option>
                        <option value="híbrido enchufable">{t('registro_conductor_tipo_hibrido_enchufable')}</option>
                        <option value="GNC/GLP">{t('registro_conductor_tipo_gnc')}</option>
                        <option value="eléctrico">{t('registro_conductor_tipo_electrico')}</option>
                        <option value="hidrógeno">{t('registro_conductor_tipo_hidrogeno')}</option>
                    </Select>
                </FormControl>

                <FormControl display="flex" alignItems="center" mb={4}>
                    <FormLabel htmlFor="sillita_bebe" mb="0" mr={2}>
                        {t('registro_conductor_sillita')}
                    </FormLabel>
                    <Checkbox id="sillita_bebe" name="sillita_bebe" isChecked={formData.sillita_bebe} onChange={handleChange} />
                </FormControl>

                <FormControl display="flex" alignItems="center" mb={4}>
                    <FormLabel htmlFor="apto_minusvalidos" mb="0" mr={2}>
                        {t('registro_conductor_minusvalidos')}
                    </FormLabel>
                    <Checkbox id="apto_minusvalidos" name="apto_minusvalidos" isChecked={formData.apto_minusvalidos} onChange={handleChange} />
                </FormControl>

                <FormControl mb={4}>
                    <FormLabel htmlFor="idioma">{t('registro_conductor_idioma')}</FormLabel>
                    <Select id="idioma" name="idioma" value={formData.idioma} onChange={handleChange}>
                        <option value="es">{t('registro_conductor_idioma_es')}</option>
                        <option value="en">{t('registro_conductor_idioma_en')}</option>
                        <option value="fr">{t('registro_conductor_idioma_fr')}</option>
                        <option value="de">{t('registro_conductor_idioma_de')}</option>
                    </Select>
                </FormControl>

                <FormControl display="flex" alignItems="center" mb={4} isRequired>
                    <FormLabel htmlFor="acepto_politicas" mb="0" mr={2}>
                        {t('registro_conductor_acepto_politicas')}
                    </FormLabel>
                    <Checkbox
                        id="acepto_politicas"
                        name="acepto_politicas"
                        isChecked={formData.acepto_politicas}
                        onChange={handleChange}
                    />
                </FormControl>

                <FormControl display="flex" alignItems="center" mb={4} isRequired>
                    <FormLabel htmlFor="declaracion_responsable" mb="0" mr={2}>
                        {t('registro_conductor_declaracion')}
                    </FormLabel>
                    <Checkbox
                        id="declaracion_responsable"
                        name="declaracion_responsable"
                        isChecked={formData.declaracion_responsable}
                        onChange={handleChange}
                    />
                </FormControl>

                <FormControl display="flex" alignItems="center" mb={4} isRequired>
                    <FormLabel htmlFor="acepto_normativa_over" mb="0" mr={2}>
                        {t('registro_conductor_acepto_normativa')}
                    </FormLabel>
                    <Checkbox
                        id="acepto_normativa_over"
                        name="acepto_normativa_over"
                        isChecked={formData.acepto_normativa_over}
                        onChange={handleChange}
                    />
                </FormControl>

                <Button
                    type="submit"
                    colorScheme="blue"
                    isLoading={isSubmitting}
                    isDisabled={!isFormValid || isSubmitting}
                    mt={4}
                >
                    {t('registro_conductor_registrar')}
                </Button>
            </form>
        </Box>
    );
};

export default RegistroConductor;